import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { PriceChart } from "../components/PriceChart";
import type { ResearchReportRow } from "../api/dataSource";
import type {
  Account,
  ChartSnapshot,
  DecisionReflection,
  EvaluationResult,
  MultiAgentReviewResult,
  RunAgentReviewResult,
  AIWorkflowStatus,
  ShadowAgentReviewRunResult,
  SocialSentimentSnapshot,
  SavedResearchEvaluation,
  ResearchSuggestedSignal,
} from "../types";

type Props = {
  listResearchReports: (
    limit?: number,
    symbol?: string,
  ) => Promise<ResearchReportRow[]>;
  listSavedResearchEvaluations: (
    limit?: number,
    symbol?: string,
  ) => Promise<SavedResearchEvaluation[]>;
  evaluateSymbol: (
    symbol: string,
    horizon: "day_swing" | "investment" | "both",
    asOf?: string,
  ) => Promise<EvaluationResult>;
  acceptEvaluation: (evaluationId: string) => Promise<void>;
  createApprovalFromEvaluation: (args: {
    evaluationId: string;
    brokerAccountId: string;
    sizedShares: string;
    accountEquitySnapshot: string;
  }) => Promise<{ approvalId: string; symbol: string }>;
  runAgentReview: (
    evaluationId: string,
    includeSocialSentiment: boolean,
    resumeWorkflowRunId?: string,
  ) => Promise<RunAgentReviewResult>;
  runShadowAgentReviews: (args: {
    symbol?: string;
    limit?: number;
    acceptedOnly?: boolean;
    includeSocialSentiment?: boolean;
    resumeWorkflowRunId?: string;
  }) => Promise<ShadowAgentReviewRunResult>;
  getLatestWorkflowStatus: (args: {
    workflowType: string;
    symbol?: string;
    feature: string;
    asOfDate?: string;
  }) => Promise<AIWorkflowStatus>;
  resumeBatchResearchEnrichment: (workflowRunId: string) => Promise<unknown>;
  getChartSnapshot: (
    symbol: string,
    lookback?: number,
  ) => Promise<ChartSnapshot>;
  listDecisionReflections: (
    limit?: number,
    symbol?: string,
  ) => Promise<DecisionReflection[]>;
  generateDecisionReflections: (args: {
    symbol?: string;
    holdingWindowDays?: number;
    benchmarkSymbol?: string;
    limit?: number;
    acceptedOnly?: boolean;
    sourceTypes?: string[];
  }) => Promise<DecisionReflection[]>;
  accounts: Account[];
};

const fmtDate = (iso: string | null): string => {
  if (iso === null) return "pending";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtCost = (cost: number): string =>
  Number.isFinite(cost) ? `$${cost.toFixed(4)}` : "$0.0000";

const fmtPrice = (price: number | null): string =>
  price === null || !Number.isFinite(price) ? "—" : `$${price.toFixed(2)}`;

const fmtUnknown = (value: unknown): string =>
  value === null || value === undefined || value === "" ? "—" : String(value);

const fmtScoreValue = (value: unknown): string => {
  if (typeof value === "number") return value.toFixed(1);
  if (typeof value === "string" && value.trim() !== "") return value;
  return "—";
};

const workflowRunId = (workflow: Record<string, unknown> | null): string | null =>
  typeof workflow?.workflow_run_id === "string" ? workflow.workflow_run_id : null;

const workflowStatus = (workflow: Record<string, unknown> | null): string =>
  typeof workflow?.status === "string" ? workflow.status : "unknown";

const shortId = (id: string | null): string => {
  if (id === null) return "none";
  return id.length > 12 ? `${id.slice(0, 8)}...${id.slice(-4)}` : id;
};

const sectionLabel = (
  key: string,
  labels: Record<string, string> = {},
): string => {
  if (labels[key]) return labels[key];
  const match = /^section_(\d+)$/.exec(key);
  return match ? `Section ${match[1]}` : key.replaceAll("_", " ");
};

const sectionEntries = (report: ResearchReportRow): [string, string][] =>
  Object.entries(report.reportSections)
    .filter(([, text]) => text.trim() !== "")
    .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));

const statusKind = (report: ResearchReportRow): string => {
  if (!report.validatorPassed) return "bad";
  if (report.supersededBy !== null || report.cachedExpired) return "warn";
  return "ok";
};

const statusText = (report: ResearchReportRow): string => {
  if (!report.validatorPassed) return "failed";
  if (report.supersededBy !== null) return "superseded";
  if (report.cachedExpired) return "expired";
  return "active";
};

const stanceColor = (
  stance: "buy" | "hold" | "avoid" | "long" | "short" | "flat",
): string => {
  if (stance === "buy" || stance === "long") return "ok";
  if (stance === "avoid" || stance === "short") return "bad";
  return "idle";
};

const voteColor = (
  vote: "bullish" | "bearish" | "neutral",
): string => {
  if (vote === "bullish") return "ok";
  if (vote === "bearish") return "bad";
  return "idle";
};

const sourceStatusKind = (status: unknown): string => {
  const text = String(status ?? "").toLowerCase();
  if (["available", "ready", "ok", "configured"].includes(text)) return "ok";
  if (["partial", "missing", "unavailable", "failed"].includes(text)) return "warn";
  return "idle";
};

const aiMemoLabel = (result: EvaluationResult): string => {
  if (result.aiMemoStatus === "ready") return "AI memo ready";
  if (result.aiMemoStatus === "failed") return "AI unavailable";
  return "AI unavailable";
};

const tradePlanTargets = (result: EvaluationResult): string => {
  if (result.tradePlan.action === "wait") return "—";
  if (Array.isArray(result.tradePlan.targets)) {
    const targets = result.tradePlan.targets.map(fmtUnknown).filter((v) => v !== "—");
    return targets.length > 0 ? targets.join(" / ") : "—";
  }
  return "—";
};

const sourceLabel = (note: Record<string, unknown>): string =>
  fmtUnknown(note.label ?? note.id ?? note.source ?? "source");

const sourceDetail = (note: Record<string, unknown>): string =>
  fmtUnknown(note.detail ?? note.reason ?? note.status ?? "");

const AnalystList = ({
  title,
  rows,
}: {
  title: string;
  rows: string[];
}) => (
  <div className="tp-workbench-analysis-card">
    <span className="label">{title}</span>
    {rows.length === 0 ? (
      <p className="tp-muted">No data.</p>
    ) : (
      <ul>
        {rows.map((row) => (
          <li key={row}>{row}</li>
        ))}
      </ul>
    )}
  </div>
);

const ScoreAttribution = ({
  attribution,
}: {
  attribution: Record<string, unknown>;
}) => {
  const rows = Object.entries(attribution).filter(
    ([, value]) => typeof value !== "object" || value === null,
  );
  return (
    <div className="tp-workbench-analysis-card">
      <span className="label">Score Attribution</span>
      {rows.length === 0 ? (
        <p className="tp-muted">No attribution details.</p>
      ) : (
        <ul>
          {rows.map(([key, value]) => (
            <li key={key}>
              <span>{key.replaceAll("_", " ")}</span>
              <small>{fmtScoreValue(value)}</small>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const SourceNotes = ({
  notes,
}: {
  notes: Record<string, unknown>[];
}) => (
  <div className="tp-workbench-analysis-card">
    <span className="label">Sources / Filings</span>
    {notes.length === 0 ? (
      <p className="tp-muted">No source notes returned.</p>
    ) : (
      <ul>
        {notes.map((note, idx) => (
          <li key={`${sourceLabel(note)}-${idx}`}>
            <span>{sourceLabel(note)}</span>
            <small>
              <span
                className={`tp-status tp-status-compact ${sourceStatusKind(
                  note.status,
                )}`}
              >
                <span className="dot" />
                {fmtUnknown(note.status)}
              </span>
              {sourceDetail(note) ? ` · ${sourceDetail(note)}` : ""}
            </small>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const AgentReviewPanel = ({
  review,
  socialSnapshot,
  workflow,
  debateOpen,
  onToggleDebate,
}: {
  review: MultiAgentReviewResult;
  socialSnapshot: SocialSentimentSnapshot | null;
  workflow: Record<string, unknown> | null;
  debateOpen: boolean;
  onToggleDebate: () => void;
}) => (
  <div className="tp-workbench-analysis-main">
    <div className="tp-tile-head" style={{ padding: 0 }}>
      <span className="label">Agent Review</span>
      <span className="tp-muted">
        {review.modelId} · {fmtDate(review.createdAt)}
      </span>
    </div>
    <p>{review.synthesisSummary}</p>
    <div className="tp-workbench-driver-list">
      <span className="tp-chip">score {review.sourceCompositeScore}</span>
      {typeof workflow?.status === "string" && (
        <span className="tp-chip">{String(workflow.status)}</span>
      )}
      {socialSnapshot && (
        <span className="tp-chip">
          social {socialSnapshot.postCount} · bull{" "}
          {socialSnapshot.counts.bullish ?? 0} / bear{" "}
          {socialSnapshot.counts.bearish ?? 0}
        </span>
      )}
      {review.citations.slice(0, 6).map((citation) => (
        <span key={citation} className="tp-chip">
          {citation}
        </span>
      ))}
    </div>
    <div className="tp-workbench-analysis-grid">
      <AnalystList title="Opportunities" rows={review.keyOpportunities} />
      <AnalystList title="Review Risks" rows={review.keyRisks} />
    </div>
    <div className="tp-actionbar">
      <button
        type="button"
        className="tp-btn tp-btn-secondary tp-btn-sm"
        onClick={onToggleDebate}
      >
        {debateOpen ? "Hide Debate Trace" : "View Debate Trace"}
      </button>
    </div>
    {debateOpen && (
      <div className="tp-workbench-analysis-grid">
        {review.agentSteps.map((step) => (
          <div key={step.role} className="tp-workbench-analysis-card">
            <span className="label">{step.role.replaceAll("_", " ")}</span>
            <p>{step.summary}</p>
            <ul>
              {[...step.keyPoints, ...step.concerns].slice(0, 6).map((row) => (
                <li key={row}>{row}</li>
              ))}
            </ul>
            <small className="tp-muted">
              {step.stance}
              {step.sourceIds.length > 0
                ? ` · ${step.sourceIds.join(", ")}`
                : ""}
            </small>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Workbench panel
// ─────────────────────────────────────────────────────────────────────────────

const WorkbenchPanel = ({
  evaluateSymbol,
  acceptEvaluation,
  createApprovalFromEvaluation,
  runAgentReview,
  getChartSnapshot,
  accounts,
  onEvaluationSaved,
}: Omit<
  Props,
  | "listResearchReports"
  | "listSavedResearchEvaluations"
  | "listDecisionReflections"
  | "generateDecisionReflections"
  | "runShadowAgentReviews"
  | "getLatestWorkflowStatus"
  | "resumeBatchResearchEnrichment"
> & {
  onEvaluationSaved: () => void;
}) => {
  const [symbol, setSymbol] = useState("");
  const [horizon, setHorizon] = useState<"day_swing" | "investment" | "both">(
    "both",
  );
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [result, setResult] = useState<EvaluationResult | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [includeSocialSentiment, setIncludeSocialSentiment] = useState(false);
  const [agentReview, setAgentReview] = useState<MultiAgentReviewResult | null>(
    null,
  );
  const [socialSnapshot, setSocialSnapshot] =
    useState<SocialSentimentSnapshot | null>(null);
  const [agentWorkflow, setAgentWorkflow] =
    useState<Record<string, unknown> | null>(null);
  const [agentRunning, setAgentRunning] = useState(false);
  const [agentError, setAgentError] = useState<string | null>(null);
  const [debateOpen, setDebateOpen] = useState(false);

  // Chart snapshot fetched after a successful evaluation; eval
  // chartLevels + chartMarkers are merged on top so the suggested
  // setup is visible as horizontal lines + markers on the price chart.
  const [chartSnapshot, setChartSnapshot] = useState<ChartSnapshot | null>(
    null,
  );
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState<string | null>(null);
  const [chartFitKey, setChartFitKey] = useState(0);

  // Approval form state
  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(
    accounts.length > 0 ? accounts[0].brokerAccountId : "",
  );
  const [sizedShares, setSizedShares] = useState("");
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [approvalDone, setApprovalDone] = useState<string | null>(null);

  const onRun = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const sym = symbol.trim().toUpperCase();
    if (!sym) return;
    setRunning(true);
    setRunError(null);
    setResult(null);
    setSaved(false);
    setShowApprovalForm(false);
    setApprovalDone(null);
    setAgentReview(null);
    setSocialSnapshot(null);
    setAgentWorkflow(null);
    setAgentError(null);
    setDebateOpen(false);
    setChartSnapshot(null);
    setChartError(null);
    try {
      const ev = await evaluateSymbol(sym, horizon);
      setResult(ev);

      // Fetch the chart snapshot in parallel with rendering the rest
      // of the panel — the bars take longer than the evaluation and we
      // want the stance/votes visible immediately. Failures here do NOT
      // block the evaluation result; we surface an inline chart error.
      setChartLoading(true);
      try {
        const snap = await getChartSnapshot(sym);
        // Merge evaluation overlays on top of the chart snapshot so
        // the suggested entry/stop/targets render as horizontal lines
        // and the signal marker renders on the bar series ALONGSIDE
        // any existing broker / open-trade overlays already present
        // on the snapshot.  De-dupe by id so a re-run can't stack
        // duplicate eval_entry/eval_stop/eval_t* levels — the eval
        // overlays always win when ids collide.
        const evalLevelIds = new Set(ev.chartLevels.map((l) => l.id));
        const evalMarkerIds = new Set(ev.chartMarkers.map((m) => m.id));
        setChartSnapshot({
          ...snap,
          levels: [
            ...snap.levels.filter((l) => !evalLevelIds.has(l.id)),
            ...ev.chartLevels,
          ],
          markers: [
            ...snap.markers.filter(
              (m) => m.kind !== "signal" && !evalMarkerIds.has(m.id),
            ),
            ...ev.chartMarkers,
          ],
        });
        setChartFitKey((n) => n + 1);
      } catch (chartErr) {
        setChartError(
          chartErr instanceof Error
            ? chartErr.message
            : "Failed to load chart snapshot",
        );
      } finally {
        setChartLoading(false);
      }
    } catch (err) {
      setRunError(err instanceof Error ? err.message : "Evaluation failed");
    } finally {
      setRunning(false);
    }
  };

  const onSave = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await acceptEvaluation(result.evaluationId);
      setSaved(true);
      onEvaluationSaved();
    } catch (err) {
      setRunError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onRunAgentReview = async (resumeWorkflowRunId?: string) => {
    if (!result) return;
    setAgentRunning(true);
    setAgentError(null);
    try {
      const reviewResult = await runAgentReview(
        result.evaluationId,
        includeSocialSentiment,
        resumeWorkflowRunId,
      );
      setAgentReview(reviewResult.review);
      setSocialSnapshot(reviewResult.socialSentiment);
      setAgentWorkflow(reviewResult.workflow);
      setDebateOpen(false);
    } catch (err) {
      setAgentError(
        err instanceof Error ? err.message : "Agent review failed",
      );
    } finally {
      setAgentRunning(false);
    }
  };

  const onOpenApproval = () => {
    if (!result) return;
    setApprovalError(null);
    if (!selectedAccount && accounts.length > 0) {
      setSelectedAccount(accounts[0].brokerAccountId);
    }
    setShowApprovalForm(true);
  };

  const onCreateApproval = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!result || !selectedAccount || !sizedShares) return;
    setApprovalError(null);
    // Resolve account equity from the selected account row so the risk
    // engine has a non-zero denominator for risk-pct math.  Refuse to
    // submit when the equity is missing or non-positive — sending "0"
    // would silently degrade every downstream cap.
    const acct = accounts.find((a) => a.brokerAccountId === selectedAccount);
    if (!acct || !Number.isFinite(acct.equity) || acct.equity <= 0) {
      setApprovalError(
        "Selected account has no positive equity snapshot — cannot create approval.",
      );
      return;
    }
    try {
      const res = await createApprovalFromEvaluation({
        evaluationId: result.evaluationId,
        brokerAccountId: selectedAccount,
        sizedShares,
        accountEquitySnapshot: String(acct.equity),
      });
      setApprovalDone(res.approvalId);
      setSaved(true);
      setShowApprovalForm(false);
      onEvaluationSaved();
    } catch (err) {
      setApprovalError(
        err instanceof Error ? err.message : "Failed to create approval",
      );
    }
  };

  return (
    <div className="tp-tile tp-workbench-panel">
      <div className="tp-tile-head">
        <span className="label">Symbol Workbench</span>
        {result && (
          <span className="tp-muted">
            {result.symbol} · {result.asOfDate}
          </span>
        )}
      </div>
      <div className="tp-tile-body">
        <form className="tp-actionbar" onSubmit={onRun}>
          <input
            className="tp-input tp-research-symbol-input"
            value={symbol}
            placeholder="Ticker (e.g. AAPL)"
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
          />
          <select
            className="tp-select"
            value={horizon}
            onChange={(e) =>
              setHorizon(
                e.target.value as "day_swing" | "investment" | "both",
              )
            }
          >
            <option value="both">Both</option>
            <option value="day_swing">Day / Swing</option>
            <option value="investment">Investment</option>
          </select>
          <button
            type="submit"
            className="tp-btn tp-btn-primary tp-btn-sm"
            disabled={running || symbol.trim() === ""}
          >
            {running ? "Running…" : "Run Research"}
          </button>
          {runError && (
            <span className="tp-inline-status bad">{runError}</span>
          )}
        </form>

        {result && (
          <div className="tp-workbench-result">
            {/* ── Stance summary ── */}
            <div className="tp-workbench-stance">
              <div className="tp-workbench-stance-grid">
                <div>
                  <span className="label">Invest</span>
                  <span
                    className={`tp-status tp-status-compact ${stanceColor(
                      result.investmentStance,
                    )}`}
                  >
                    <span className="dot" />
                    {result.investmentStance.toUpperCase()}
                  </span>
                </div>
                <div>
                  <span className="label">Trade</span>
                  <span
                    className={`tp-status tp-status-compact ${stanceColor(
                      result.tradeStance,
                    )}`}
                  >
                    <span className="dot" />
                    {result.tradeStance.toUpperCase()}
                  </span>
                </div>
                <div>
                  <span className="label">Score</span>
                  <span className="num">{result.compositeScore}</span>
                </div>
                <div>
                  <span className="label">Conf</span>
                  <span className="num">{result.confidence}</span>
                </div>
                <div>
                  <span className="label">Regime</span>
                  <span className="num">
                    {result.regime.replace(/_/g, " ")}
                  </span>
                </div>
                <div>
                  <span className="label">Live</span>
                  <span className="num">{fmtPrice(result.livePrice)}</span>
                </div>
                <div>
                  <span className="label">Last bar</span>
                  <span className="num">
                    {result.latestBarDate ?? result.asOfDate}
                  </span>
                </div>
                <div>
                  <span className="label">Bar close</span>
                  <span className="num">
                    {fmtPrice(result.latestBarClose)}
                  </span>
                </div>
                <div>
                  <span className="label">Latest news</span>
                  <span className="num">
                    {result.latestNewsAt ? fmtDate(result.latestNewsAt) : "—"}
                  </span>
                </div>
                <div>
                  <span className="label">Evaluated</span>
                  <span className="num">{fmtDate(result.evaluatedAt)}</span>
                </div>
              </div>

              {result.entryPrice !== null && (
                <div className="tp-workbench-setup">
                  <div>
                    <span className="label">Entry</span>
                    <span className="num">{fmtPrice(result.entryPrice)}</span>
                  </div>
                  <div>
                    <span className="label">Stop</span>
                    <span className="num bad">{fmtPrice(result.stopPrice)}</span>
                  </div>
                  <div className="wide">
                    <span className="label">Targets</span>
                    <span className="num ok">
                      {result.targetPrices.length > 0
                        ? result.targetPrices.map(fmtPrice).join(" / ")
                        : "—"}
                    </span>
                  </div>
                </div>
              )}

              {result.invalidationText && (
                <div className="tp-workbench-invalidation tp-muted">
                  {result.invalidationText}
                </div>
              )}
              {result.scoreDrivers.length > 0 && (
                <div className="tp-workbench-driver-list">
                  {result.scoreDrivers.slice(0, 10).map((driver) => (
                    <span key={driver} className="tp-chip">
                      {driver.replaceAll("_", " ")}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* ── Context badges ── */}
            <div className="tp-workbench-badges">
              {result.hasFundamentals && (
                <span className="tp-chip">fundamentals</span>
              )}
              {result.hasMacro && <span className="tp-chip">macro</span>}
              {result.newsCount > 0 && (
                <span className="tp-chip">{result.newsCount} news</span>
              )}
              {result.livePriceSource !== "unavailable" && (
                <span className="tp-chip">live quote</span>
              )}
              {result.portfolioAlreadyHeld && (
                <span className="tp-chip warn">already held</span>
              )}
              {result.isEtf && <span className="tp-chip">ETF</span>}
              <span
                className={
                  result.aiMemoStatus === "ready"
                    ? "tp-chip"
                    : "tp-chip warn"
                }
              >
                {aiMemoLabel(result)}
              </span>
              {result.sourceNotes.map((note, idx) => (
                <span
                  key={`${sourceLabel(note)}-${idx}`}
                  className={`tp-chip ${sourceStatusKind(note.status) === "ok" ? "" : "warn"}`}
                >
                  {sourceLabel(note)} {fmtUnknown(note.status)}
                </span>
              ))}
            </div>

            <div className="tp-actionbar">
              <label className="tp-checkbox-row">
                <input
                  type="checkbox"
                  checked={includeSocialSentiment}
                  onChange={(e) => setIncludeSocialSentiment(e.target.checked)}
                />
                <span>Include Social Sentiment</span>
              </label>
              <button
                type="button"
                className="tp-btn tp-btn-secondary tp-btn-sm"
                disabled={agentRunning}
                onClick={() => void onRunAgentReview()}
              >
                {agentRunning ? "Review Running…" : "Run Agent Review"}
              </button>
              {agentReview?.workflowRunId && (
                <button
                  type="button"
                  className="tp-btn tp-btn-secondary tp-btn-sm"
                  disabled={agentRunning}
                  onClick={() =>
                    void onRunAgentReview(agentReview.workflowRunId ?? undefined)
                  }
                >
                  Resume Review
                </button>
              )}
              {agentError && (
                <span className="tp-inline-status bad">{agentError}</span>
              )}
            </div>

            {agentReview && (
              <AgentReviewPanel
                review={agentReview}
                socialSnapshot={socialSnapshot}
                workflow={agentWorkflow}
                debateOpen={debateOpen}
                onToggleDebate={() => setDebateOpen((open) => !open)}
              />
            )}

            <div className="tp-workbench-analyst">
              <div className="tp-workbench-analysis-main">
                <span className="label">Analyst view</span>
                <p>{result.analystSummary || "No analyst summary available."}</p>
                <div className="tp-workbench-trade-plan">
                  <span>
                    Action: <strong>{fmtUnknown(result.tradePlan.action)}</strong>
                  </span>
                  <span>Entry: {fmtUnknown(result.tradePlan.entry)}</span>
                  <span>Stop: {fmtUnknown(result.tradePlan.stop)}</span>
                  <span>
                    Targets: {tradePlanTargets(result)}
                  </span>
                  {result.tradePlan.first_target_r_multiple !== undefined &&
                    result.tradePlan.first_target_r_multiple !== null && (
                    <span>
                      R: {fmtUnknown(result.tradePlan.first_target_r_multiple)}
                    </span>
                  )}
                </div>
                {result.aiMemoStatus === "failed" && result.aiMemoError && (
                  <div className="tp-inline-status bad">
                    AI memo failed: {result.aiMemoError}
                  </div>
                )}
                {result.aiMemoStatus === "ready" && (
                  <div className="tp-inline-status ok">
                    AI memo ready
                    {result.aiMemoModel ? ` · ${result.aiMemoModel}` : ""}
                    {result.aiMemoCost ? ` · ${result.aiMemoCost}` : ""}
                  </div>
                )}
              </div>
              <div className="tp-workbench-analysis-grid">
                <ScoreAttribution attribution={result.scoreAttribution} />
                <AnalystList title="Bull Case" rows={result.bullCase} />
                <AnalystList title="Bear Case" rows={result.bearCase} />
                <AnalystList title="Key Risks" rows={result.keyRisks} />
                <AnalystList
                  title="Valuation"
                  rows={result.valuationSummary}
                />
                <AnalystList
                  title="Sector / Peers"
                  rows={result.sectorPeerSummary}
                />
                <AnalystList
                  title="Fundamentals"
                  rows={result.fundamentalsSummary}
                />
                <AnalystList title="Macro" rows={result.macroSummary} />
                <div className="tp-workbench-analysis-card">
                  <span className="label">News / Catalysts</span>
                  {result.newsDigest.length === 0 ? (
                    <p className="tp-muted">No recent headlines in this run.</p>
                  ) : (
                    <ul>
                      {result.newsDigest.slice(0, 5).map((item, idx) => (
                        <li key={`${fmtUnknown(item.headline)}-${idx}`}>
                          <span>{fmtUnknown(item.headline)}</span>
                          <small>
                            {fmtUnknown(item.source)}
                            {item.published_at
                              ? ` · ${fmtDate(String(item.published_at))}`
                              : ""}
                          </small>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <AnalystList
                  title="Catalyst Status"
                  rows={result.newsCatalysts}
                />
                <SourceNotes notes={result.sourceNotes} />
              </div>
            </div>

            {/* ── Price chart with eval overlays ── */}
            <div className="tp-workbench-chart">
              <div className="tp-workbench-chart-head">
                <div>
                  <span className="label">Suggested setup chart</span>
                  <span className="tp-muted">
                    {result.symbol} · entry / stop / target overlays
                  </span>
                </div>
                {chartSnapshot && (
                  <span className="tp-muted">
                    {chartSnapshot.bars.length} bars
                  </span>
                )}
              </div>
              {chartLoading && (
                <div className="tp-muted tp-workbench-chart-status">
                  Loading chart…
                </div>
              )}
              {chartError && !chartLoading && (
                <div className="tp-inline-status bad">{chartError}</div>
              )}
              {chartSnapshot && !chartError && (
                <PriceChart
                  snapshot={chartSnapshot}
                  showSma={true}
                  showRsi={false}
                  showMarkers={true}
                  showLevels={true}
                  fitKey={chartFitKey}
                  mode="candles"
                  height={360}
                />
              )}
            </div>

            {/* ── Strategy vote table ── */}
            <div className="tp-table-scroll">
              <table className="tp-table tp-workbench-votes-table">
                <thead>
                  <tr>
                    <th>Strategy</th>
                    <th>Vote</th>
                    <th className="right">Score</th>
                    <th>Factors</th>
                  </tr>
                </thead>
                <tbody>
                  {result.strategyVotes.map((v) => (
                    <tr key={v.strategyId}>
                      <td className="tp-muted" style={{ fontSize: "0.8em" }}>
                        {v.strategyId}
                      </td>
                      <td>
                        <span
                          className={`tp-status tp-status-compact ${voteColor(
                            v.vote,
                          )}`}
                        >
                          <span className="dot" />
                          {v.vote}
                        </span>
                      </td>
                      <td className="right num">{v.score || "—"}</td>
                      <td className="tp-muted">
                        {v.factors.length > 0
                          ? v.factors.join(", ")
                          : v.reason || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Action bar ── */}
            <div className="tp-actionbar" style={{ marginTop: "0.75rem" }}>
              {!saved && (
                <button
                  type="button"
                  className="tp-btn tp-btn-secondary tp-btn-sm"
                  disabled={saving}
                  onClick={() => void onSave()}
                >
                  {saving ? "Saving…" : "Save Research"}
                </button>
              )}
              {saved && (
                <span className="tp-inline-status ok">Saved</span>
              )}
              {result.tradeStance !== "flat" &&
                result.entryPrice !== null &&
                result.stopPrice !== null && (
                  <button
                    type="button"
                    className="tp-btn tp-btn-primary tp-btn-sm"
                    onClick={onOpenApproval}
                  >
                    Create Approval ▶
                  </button>
                )}
              {approvalDone && (
                <span className="tp-inline-status ok">
                  Approval created · {shortId(approvalDone)}
                </span>
              )}
            </div>

            {/* ── Inline approval form ── */}
            {showApprovalForm && (
              <form
                className="tp-workbench-approval-form"
                onSubmit={(e) => void onCreateApproval(e)}
              >
                <label>
                  Shares
                  <input
                    className="tp-input"
                    type="number"
                    min="1"
                    step="1"
                    value={sizedShares}
                    onChange={(e) => setSizedShares(e.target.value)}
                    placeholder="e.g. 10"
                    required
                  />
                </label>
                {accounts.length > 0 && (
                  <label>
                    Account
                    <select
                      className="tp-select"
                      value={selectedAccount}
                      onChange={(e) => setSelectedAccount(e.target.value)}
                    >
                      {accounts.map((a) => (
                        <option key={a.brokerAccountId} value={a.brokerAccountId}>
                          {a.label} ({a.broker})
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <button
                  type="submit"
                  className="tp-btn tp-btn-primary tp-btn-sm"
                  disabled={!sizedShares || !selectedAccount}
                >
                  Submit
                </button>
                <button
                  type="button"
                  className="tp-btn tp-btn-secondary tp-btn-sm"
                  onClick={() => setShowApprovalForm(false)}
                >
                  Cancel
                </button>
                {approvalError && (
                  <span className="tp-inline-status bad">{approvalError}</span>
                )}
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Saved workbench evaluations
// ─────────────────────────────────────────────────────────────────────────────

const SavedResearchPanel = ({
  listSavedResearchEvaluations,
  listDecisionReflections,
  generateDecisionReflections,
  runShadowAgentReviews,
  getLatestWorkflowStatus,
  resumeBatchResearchEnrichment,
  reloadKey,
}: {
  listSavedResearchEvaluations: Props["listSavedResearchEvaluations"];
  listDecisionReflections: Props["listDecisionReflections"];
  generateDecisionReflections: Props["generateDecisionReflections"];
  runShadowAgentReviews: Props["runShadowAgentReviews"];
  getLatestWorkflowStatus: Props["getLatestWorkflowStatus"];
  resumeBatchResearchEnrichment: Props["resumeBatchResearchEnrichment"];
  reloadKey: number;
}) => {
  const [rows, setRows] = useState<SavedResearchEvaluation[]>([]);
  const [reflections, setReflections] = useState<DecisionReflection[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filterText, setFilterText] = useState("");
  const [appliedSymbol, setAppliedSymbol] = useState("");
  const [loading, setLoading] = useState(true);
  const [reflectionLoading, setReflectionLoading] = useState(false);
  const [reflectionStatus, setReflectionStatus] = useState<string | null>(null);
  const [shadowRunning, setShadowRunning] = useState(false);
  const [shadowResult, setShadowResult] =
    useState<ShadowAgentReviewRunResult | null>(null);
  const [shadowError, setShadowError] = useState<string | null>(null);
  const [batchWorkflowLoading, setBatchWorkflowLoading] = useState(false);
  const [batchResumeLoading, setBatchResumeLoading] = useState(false);
  const [batchWorkflow, setBatchWorkflow] =
    useState<AIWorkflowStatus | null>(null);
  const [batchWorkflowError, setBatchWorkflowError] = useState<string | null>(
    null,
  );
  const [reflectionSourceTypes, setReflectionSourceTypes] = useState<string[]>([
    "research_evaluation",
  ]);
  const [error, setError] = useState<string | null>(null);
  const [reflectionError, setReflectionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await listSavedResearchEvaluations(
        100,
        appliedSymbol === "" ? undefined : appliedSymbol,
      );
      const nextReflections = await listDecisionReflections(
        20,
        appliedSymbol === "" ? undefined : appliedSymbol,
      );
      setRows(next);
      setReflections(nextReflections);
      setSelectedId((current) => {
        if (current !== null && next.some((row) => row.evaluationId === current)) {
          return current;
        }
        return next[0]?.evaluationId ?? null;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load saved research");
    } finally {
      setLoading(false);
    }
  }, [appliedSymbol, listDecisionReflections, listSavedResearchEvaluations]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  const selected = useMemo(
    () =>
      rows.find((row) => row.evaluationId === selectedId) ??
      rows[0] ??
      null,
    [rows, selectedId],
  );

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAppliedSymbol(filterText.trim().toUpperCase());
  };

  const onGenerateReflections = async () => {
    setReflectionLoading(true);
    setReflectionError(null);
    setReflectionStatus(null);
    try {
      const generated = await generateDecisionReflections({
        symbol: appliedSymbol === "" ? undefined : appliedSymbol,
        holdingWindowDays: 14,
        benchmarkSymbol: "SPY",
        limit: 100,
        acceptedOnly: true,
        sourceTypes: reflectionSourceTypes,
      });
      const latest = await listDecisionReflections(
        20,
        appliedSymbol === "" ? undefined : appliedSymbol,
      );
      setReflections(latest);
      setReflectionStatus(`generated ${generated.length}`);
    } catch (e) {
      setReflectionError(
        e instanceof Error ? e.message : "Failed to generate reflections",
      );
    } finally {
      setReflectionLoading(false);
    }
  };

  const onRunShadowReviews = async (resumeWorkflowRunId?: string) => {
    setShadowRunning(true);
    setShadowError(null);
    try {
      const result = await runShadowAgentReviews({
        symbol: appliedSymbol === "" ? undefined : appliedSymbol,
        limit: 25,
        acceptedOnly: true,
        includeSocialSentiment: false,
        resumeWorkflowRunId,
      });
      setShadowResult(result);
    } catch (e) {
      setShadowError(
        e instanceof Error ? e.message : "Failed to run shadow reviews",
      );
    } finally {
      setShadowRunning(false);
    }
  };

  const onLoadBatchWorkflow = async () => {
    setBatchWorkflowLoading(true);
    setBatchWorkflowError(null);
    try {
      const status = await getLatestWorkflowStatus({
        workflowType: "batch_research_enrichment",
        symbol: "*",
        feature: "signal_research_enrichment",
      });
      setBatchWorkflow(status);
    } catch (e) {
      setBatchWorkflowError(
        e instanceof Error ? e.message : "No batch enrichment workflow found",
      );
    } finally {
      setBatchWorkflowLoading(false);
    }
  };

  const onResumeBatchWorkflow = async () => {
    const runId = workflowRunId(batchWorkflow?.run ?? null);
    if (!runId) return;
    setBatchResumeLoading(true);
    setBatchWorkflowError(null);
    try {
      await resumeBatchResearchEnrichment(runId);
      await onLoadBatchWorkflow();
    } catch (e) {
      setBatchWorkflowError(
        e instanceof Error ? e.message : "Failed to resume batch workflow",
      );
    } finally {
      setBatchResumeLoading(false);
    }
  };

  return (
    <div className="tp-research-grid">
      <div className="tp-tile">
        <div className="tp-tile-head">
          <span className="label">Saved workbench research</span>
          <span className="tp-muted">
            {loading ? "loading" : `${rows.length} saved`}
          </span>
        </div>
        <div className="tp-tile-body" style={{ padding: 0 }}>
          <form className="tp-actionbar" onSubmit={onSubmit}>
            <input
              className="tp-input tp-research-symbol-input"
              value={filterText}
              placeholder="Symbol"
              onChange={(e) => setFilterText(e.target.value.toUpperCase())}
            />
            <button type="submit" className="tp-btn tp-btn-secondary tp-btn-sm">
              Apply
            </button>
            <button
              type="button"
              className="tp-btn tp-btn-secondary tp-btn-sm"
              onClick={() => {
                setFilterText("");
                setAppliedSymbol("");
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="tp-btn tp-btn-secondary tp-btn-sm"
              disabled={loading}
              onClick={() => void load()}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <button
              type="button"
              className="tp-btn tp-btn-secondary tp-btn-sm"
              disabled={reflectionLoading}
              onClick={() => void onGenerateReflections()}
            >
              {reflectionLoading ? "Generating..." : "Generate Outcome Lessons"}
            </button>
            {[
              ["research_evaluation", "Research"],
              ["closed_trade", "Closed trades"],
              ["expired_signal", "Expired signals"],
              ["investment_report", "Reports"],
            ].map(([value, label]) => (
              <label key={value} className="tp-toggle-row tp-toggle-row-compact">
                <input
                  type="checkbox"
                  checked={reflectionSourceTypes.includes(value)}
                  onChange={(e) => {
                    setReflectionSourceTypes((current) => {
                      const next = e.target.checked
                        ? [...current, value]
                        : current.filter((item) => item !== value);
                      return next.length === 0 ? current : next;
                    });
                  }}
                />
                {label}
              </label>
            ))}
            {appliedSymbol !== "" && (
              <span className="tp-inline-status">filter: {appliedSymbol}</span>
            )}
            {reflectionStatus && (
              <span className="tp-inline-status">{reflectionStatus}</span>
            )}
          </form>
          <div className="tp-actionbar">
            <button
              type="button"
              className="tp-btn tp-btn-secondary tp-btn-sm"
              disabled={shadowRunning || rows.length === 0}
              onClick={() => void onRunShadowReviews()}
            >
              {shadowRunning ? "Shadow Running..." : "Run Shadow Reviews"}
            </button>
            {shadowResult?.workflow &&
              workflowStatus(shadowResult.workflow) !== "completed" &&
              workflowRunId(shadowResult.workflow) && (
                <button
                  type="button"
                  className="tp-btn tp-btn-secondary tp-btn-sm"
                  disabled={shadowRunning}
                  onClick={() =>
                    void onRunShadowReviews(
                      workflowRunId(shadowResult.workflow) ?? undefined,
                    )
                  }
                >
                  Resume Shadow Run
                </button>
              )}
            <button
              type="button"
              className="tp-btn tp-btn-secondary tp-btn-sm"
              disabled={batchWorkflowLoading}
              onClick={() => void onLoadBatchWorkflow()}
            >
              {batchWorkflowLoading ? "Loading Workflow..." : "Batch Workflow Status"}
            </button>
            {batchWorkflow?.run &&
              workflowStatus(batchWorkflow.run) !== "completed" &&
              workflowRunId(batchWorkflow.run) && (
                <button
                  type="button"
                  className="tp-btn tp-btn-secondary tp-btn-sm"
                  disabled={batchResumeLoading}
                  onClick={() => void onResumeBatchWorkflow()}
                >
                  {batchResumeLoading ? "Resuming..." : "Resume Batch Workflow"}
                </button>
              )}
            {shadowResult && (
              <span className="tp-inline-status">
                shadow reviewed {shadowResult.reviewed}, failed {shadowResult.failed}
                {shadowResult.workflow
                  ? ` · ${workflowStatus(shadowResult.workflow)}`
                  : ""}
              </span>
            )}
            {batchWorkflow && (
              <span className="tp-inline-status">
                batch {workflowStatus(batchWorkflow.run)} ·{" "}
                {batchWorkflow.steps.length} steps
              </span>
            )}
          </div>
          {error && <div className="tp-inline-status bad">{error}</div>}
          {reflectionError && (
            <div className="tp-inline-status bad">{reflectionError}</div>
          )}
          {shadowError && (
            <div className="tp-inline-status bad">{shadowError}</div>
          )}
          {batchWorkflowError && (
            <div className="tp-inline-status bad">{batchWorkflowError}</div>
          )}
          {batchWorkflow && (
            <div className="tp-workbench-analysis-grid" style={{ padding: 12 }}>
              {batchWorkflow.steps.slice(0, 6).map((step, idx) => (
                <div
                  key={`${fmtUnknown(step.step_name)}-${idx}`}
                  className="tp-workbench-analysis-card"
                >
                  <span className="label">{fmtUnknown(step.step_name)}</span>
                  <p>{fmtUnknown(step.status)}</p>
                  {step.last_error !== undefined && step.last_error !== null && (
                    <small className="tp-inline-status bad">
                      {fmtUnknown(step.last_error)}
                    </small>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="tp-table-scroll">
            <table className="tp-table tp-research-table">
              <thead>
                <tr>
                  <th>Saved</th>
                  <th>Symbol</th>
                  <th>Stance</th>
                  <th className="right">Score</th>
                  <th>AI</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="tp-empty-cell">
                      {loading ? "Loading saved research..." : "No saved research."}
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr
                      key={row.evaluationId}
                      className={
                        "row-hover" +
                        (selected?.evaluationId === row.evaluationId
                          ? " row-active"
                          : "")
                      }
                      onClick={() => setSelectedId(row.evaluationId)}
                    >
                      <td className="num">{fmtDate(row.acceptedAt)}</td>
                      <td className="ticker">{row.symbol}</td>
                      <td>
                        <span
                          className={`tp-status tp-status-compact ${stanceColor(
                            row.investmentStance,
                          )}`}
                        >
                          <span className="dot" />
                          {row.investmentStance.toUpperCase()}
                        </span>
                      </td>
                      <td className="right num">{row.compositeScore}</td>
                      <td>
                        <span
                          className={
                            row.aiMemoStatus === "ready"
                              ? "tp-status tp-status-compact ok"
                              : "tp-status tp-status-compact warn"
                          }
                        >
                          <span className="dot" />
                          {row.aiMemoStatus}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="tp-research-detail-stack">
        <SavedResearchDetail evaluation={selected} />
        <DecisionReflectionsPanel reflections={reflections} />
      </div>
    </div>
  );
};

const DecisionReflectionsPanel = ({
  reflections,
}: {
  reflections: DecisionReflection[];
}) => (
  <div className="tp-tile tp-research-detail">
    <div className="tp-tile-head">
      <span className="label">Outcome lessons</span>
      <span className="tp-muted">{reflections.length} recent</span>
    </div>
    <div className="tp-tile-body">
      {reflections.length === 0 ? (
        <div className="tp-empty-cell">No outcome lessons generated.</div>
      ) : (
        <div className="tp-workbench-analysis-grid">
          {reflections.slice(0, 4).map((reflection) => (
            <div
              key={reflection.reflectionId}
              className="tp-workbench-analysis-card"
            >
              <span className="label">
                {reflection.symbol} · {reflection.holdingWindowDays}d
              </span>
              <p>{reflection.lesson}</p>
              <div className="tp-workbench-driver-list">
                <span className="tp-chip">
                  raw {reflection.rawReturnPct}%
                </span>
                {reflection.relativeReturnPct !== null && (
                  <span className="tp-chip">
                    vs SPY {reflection.relativeReturnPct}%
                  </span>
                )}
                <span className="tp-chip">{reflection.confidence}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

const SavedResearchDetail = ({
  evaluation,
}: {
  evaluation: SavedResearchEvaluation | null;
}) => {
  if (evaluation === null) {
    return (
      <div className="tp-tile tp-research-detail">
        <div className="tp-tile-head">
          <span className="label">Saved packet</span>
          <span className="tp-muted">none</span>
        </div>
        <div className="tp-tile-body">
          <div className="tp-empty-cell">No saved evaluation selected.</div>
        </div>
      </div>
    );
  }

  const bullish = evaluation.strategyVotes.filter((v) => v.vote === "bullish").length;
  const bearish = evaluation.strategyVotes.filter((v) => v.vote === "bearish").length;
  const neutral = evaluation.strategyVotes.filter((v) => v.vote === "neutral").length;

  return (
    <div className="tp-tile tp-research-detail">
      <div className="tp-tile-head">
        <span className="label">
          {evaluation.symbol} / {shortId(evaluation.evaluationId)}
        </span>
        <span className="tp-muted">saved {fmtDate(evaluation.acceptedAt)}</span>
      </div>
      <div className="tp-tile-body">
        <div className="tp-research-meta-grid">
          <div>
            <span className="label">Invest</span>
            <span className="num">{evaluation.investmentStance}</span>
          </div>
          <div>
            <span className="label">Trade</span>
            <span className="num">{evaluation.tradeStance}</span>
          </div>
          <div>
            <span className="label">Score</span>
            <span className="num">{evaluation.compositeScore}</span>
          </div>
          <div>
            <span className="label">Conf</span>
            <span className="num">{evaluation.confidence}</span>
          </div>
          <div>
            <span className="label">Regime</span>
            <span className="num">{evaluation.regime.replaceAll("_", " ")}</span>
          </div>
          <div>
            <span className="label">Approval</span>
            <span className="num">{shortId(evaluation.approvalId)}</span>
          </div>
        </div>

        <div className="tp-workbench-analysis-main">
          <span className="label">Analyst view</span>
          <p>{evaluation.analystSummary || "No analyst summary stored."}</p>
          <div className="tp-workbench-trade-plan">
            <span>
              Action: <strong>{fmtUnknown(evaluation.tradePlan.action)}</strong>
            </span>
            <span>Entry: {fmtUnknown(evaluation.tradePlan.entry)}</span>
            <span>Stop: {fmtUnknown(evaluation.tradePlan.stop)}</span>
            <span>Targets: {tradePlanTargets(evaluation)}</span>
          </div>
          {evaluation.aiMemoStatus === "failed" && evaluation.aiMemoError && (
            <div className="tp-inline-status bad">
              AI memo failed: {evaluation.aiMemoError}
            </div>
          )}
        </div>

        <div className="tp-workbench-badges">
          <span className="tp-chip">bullish {bullish}</span>
          <span className="tp-chip">bearish {bearish}</span>
          <span className="tp-chip">neutral {neutral}</span>
          <span className="tp-chip">{aiMemoLabel(evaluation)}</span>
        </div>

        <div className="tp-workbench-analysis-grid">
          <ScoreAttribution attribution={evaluation.scoreAttribution} />
          <AnalystList title="Bull Case" rows={evaluation.bullCase} />
          <AnalystList title="Bear Case" rows={evaluation.bearCase} />
          <AnalystList title="Key Risks" rows={evaluation.keyRisks} />
          <AnalystList title="Valuation" rows={evaluation.valuationSummary} />
          <AnalystList title="Sector / Peers" rows={evaluation.sectorPeerSummary} />
          <AnalystList title="Catalysts" rows={evaluation.newsCatalysts} />
          <SourceNotes notes={evaluation.sourceNotes} />
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main screen
// ─────────────────────────────────────────────────────────────────────────────

export const ResearchScreen = ({
  listResearchReports,
  listSavedResearchEvaluations,
  evaluateSymbol,
  acceptEvaluation,
  createApprovalFromEvaluation,
  runAgentReview,
  runShadowAgentReviews,
  getLatestWorkflowStatus,
  resumeBatchResearchEnrichment,
  getChartSnapshot,
  listDecisionReflections,
  generateDecisionReflections,
  accounts,
}: Props) => {
  const [savedReloadKey, setSavedReloadKey] = useState(0);
  const [rows, setRows] = useState<ResearchReportRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filterText, setFilterText] = useState("");
  const [appliedSymbol, setAppliedSymbol] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await listResearchReports(
        500,
        appliedSymbol === "" ? undefined : appliedSymbol,
      );
      setRows(next);
      setSelectedId((current) => {
        if (current !== null && next.some((row) => row.id === current)) {
          return current;
        }
        return next[0]?.id ?? null;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load research");
    } finally {
      setLoading(false);
    }
  }, [appliedSymbol, listResearchReports]);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = useMemo(
    () =>
      rows.find((row) => row.id === selectedId) ??
      rows[0] ??
      null,
    [rows, selectedId],
  );

  const stats = useMemo(() => {
    const passed = rows.filter((row) => row.validatorPassed).length;
    const failed = rows.length - passed;
    const active = rows.filter(
      (row) =>
        row.validatorPassed &&
        !row.cachedExpired &&
        row.supersededBy === null,
    ).length;
    const cost = rows.reduce((acc, row) => acc + row.cost, 0);
    return { passed, failed, active, cost };
  }, [rows]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAppliedSymbol(filterText.trim().toUpperCase());
  };

  return (
    <div data-screen-label="Research">
      <div className="tp-page-head">
        <h1>Research</h1>
        <div className="meta">
          {loading ? "loading" : `${rows.length} reports`}
        </div>
      </div>

      {/* ── Workbench panel (above the archive) ── */}
      <WorkbenchPanel
        evaluateSymbol={evaluateSymbol}
        acceptEvaluation={acceptEvaluation}
        createApprovalFromEvaluation={createApprovalFromEvaluation}
        runAgentReview={runAgentReview}
        getChartSnapshot={getChartSnapshot}
        accounts={accounts}
        onEvaluationSaved={() => setSavedReloadKey((n) => n + 1)}
      />

      <SavedResearchPanel
        listSavedResearchEvaluations={listSavedResearchEvaluations}
        listDecisionReflections={listDecisionReflections}
        generateDecisionReflections={generateDecisionReflections}
        runShadowAgentReviews={runShadowAgentReviews}
        getLatestWorkflowStatus={getLatestWorkflowStatus}
        resumeBatchResearchEnrichment={resumeBatchResearchEnrichment}
        reloadKey={savedReloadKey}
      />

      {/* ── Archive section ── */}
      <form className="tp-actionbar" onSubmit={onSubmit}>
        <input
          className="tp-input tp-research-symbol-input"
          value={filterText}
          placeholder="Symbol"
          onChange={(e) => setFilterText(e.target.value.toUpperCase())}
        />
        <button type="submit" className="tp-btn tp-btn-secondary tp-btn-sm">
          Apply
        </button>
        <button
          type="button"
          className="tp-btn tp-btn-secondary tp-btn-sm"
          onClick={() => {
            setFilterText("");
            setAppliedSymbol("");
          }}
        >
          Clear
        </button>
        <button
          type="button"
          className="tp-btn tp-btn-secondary tp-btn-sm"
          disabled={loading}
          onClick={() => void load()}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
        {appliedSymbol !== "" && (
          <span className="tp-inline-status">filter: {appliedSymbol}</span>
        )}
        {error && <span className="tp-inline-status bad">{error}</span>}
      </form>

      <div className="tp-research-stats">
        <div>
          <span className="label">active</span>
          <span className="num">{stats.active}</span>
        </div>
        <div>
          <span className="label">passed</span>
          <span className="num">{stats.passed}</span>
        </div>
        <div>
          <span className="label">failed</span>
          <span className="num">{stats.failed}</span>
        </div>
        <div>
          <span className="label">cost</span>
          <span className="num">{fmtCost(stats.cost)}</span>
        </div>
      </div>

      <div className="tp-research-grid">
        <div className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Report archive</span>
            <span className="tp-muted">latest 500</span>
          </div>
          <div className="tp-tile-body" style={{ padding: 0 }}>
            <div className="tp-table-scroll">
              <table className="tp-table tp-research-table">
                <thead>
                  <tr>
                    <th>Generated</th>
                    <th>Symbol</th>
                    <th>Status</th>
                    <th className="right">Cost</th>
                    <th className="right">Cache</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="tp-empty-cell">
                        {loading ? "Loading research..." : "No research reports."}
                      </td>
                    </tr>
                  ) : (
                    rows.map((report) => (
                      <tr
                        key={report.id}
                        className={
                          "row-hover" +
                          (selected?.id === report.id ? " row-active" : "")
                        }
                        onClick={() => setSelectedId(report.id)}
                      >
                        <td className="num">{fmtDate(report.completedAt)}</td>
                        <td className="ticker">{report.symbol}</td>
                        <td>
                          <span
                            className={`tp-status tp-status-compact ${statusKind(
                              report,
                            )}`}
                          >
                            <span className="dot" />
                            {statusText(report)}
                          </span>
                        </td>
                        <td className="right num">{fmtCost(report.cost)}</td>
                        <td className="right num">
                          {fmtDate(report.cachedUntil)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <ReportDetail report={selected} />
      </div>
    </div>
  );
};

const ReportDetail = ({ report }: { report: ResearchReportRow | null }) => {
  if (report === null) {
    return (
      <div className="tp-tile">
        <div className="tp-tile-head">
          <span className="label">Report</span>
          <span className="tp-muted">none</span>
        </div>
        <div className="tp-tile-body">
          <div className="tp-empty-cell">No report selected.</div>
        </div>
      </div>
    );
  }

  const sections = sectionEntries(report);

  return (
    <div className="tp-tile tp-research-detail">
      <div className="tp-tile-head">
        <span className="label">
          {report.symbol} / {shortId(report.id)}
        </span>
        <span
          className={`tp-status tp-status-compact ${statusKind(report)}`}
        >
          <span className="dot" />
          {statusText(report)}
        </span>
      </div>
      <div className="tp-tile-body">
        <div className="tp-research-meta-grid">
          <div>
            <span className="label">Generated</span>
            <span className="num">{fmtDate(report.completedAt)}</span>
          </div>
          <div>
            <span className="label">Model</span>
            <span className="num">{report.modelVersion}</span>
          </div>
          <div>
            <span className="label">Prompt</span>
            <span className="num">{report.promptVersion}</span>
          </div>
          <div>
            <span className="label">Kind</span>
            <span className="num">{report.reportKind.replaceAll("_", " ")}</span>
          </div>
          <div>
            <span className="label">Asset</span>
            <span className="num">{report.assetClass}</span>
          </div>
          <div>
            <span className="label">Citations</span>
            <span className="num">{report.citationCount}</span>
          </div>
          <div>
            <span className="label">Primary quotes</span>
            <span className="num">{report.primaryDocQuoteCount}</span>
          </div>
        </div>

        {report.validatorFailures.length > 0 && (
          <div className="tp-research-failures">
            {report.validatorFailures.map((failure) => (
              <div key={failure}>{failure}</div>
            ))}
          </div>
        )}

        <div className="tp-research-source-row">
          <span className="label">Primary documents</span>
          <span className="num">{report.primaryDocuments.length}</span>
          {report.primaryDocuments.slice(0, 4).map((doc, index) => (
            <span key={index} className="tp-research-source-chip">
              {doc.form !== undefined ? String(doc.form) : `doc ${index + 1}`}
            </span>
          ))}
        </div>

        {report.suggestedSignal ? (
          <ResearchSuggestedSetupPanel signal={report.suggestedSignal} />
        ) : null}

        <div className="tp-research-report-body">
          {sections.length > 0 ? (
            sections.map(([key, text]) => (
              <section key={key} className="tp-research-section">
                <h2>{sectionLabel(key, report.sectionLabels)}</h2>
                <p>{text}</p>
              </section>
            ))
          ) : (
            <pre>{report.reportText || "No report text stored."}</pre>
          )}
        </div>
      </div>
    </div>
  );
};

const ResearchSuggestedSetupPanel = ({
  signal,
}: {
  signal: ResearchSuggestedSignal;
}) => (
  <div className="tp-workbench-analysis-main tp-research-suggested-setup">
    <div className="tp-tile-head" style={{ padding: 0 }}>
      <span className="label">Suggested setup</span>
      <span className="tp-chip info">Display only</span>
    </div>
    <div className="tp-workbench-driver-list">
      <span className={`tp-status tp-status-compact ${stanceColor(signal.stance)}`}>
        <span className="dot" />
        {signal.stance}
      </span>
      <span className="tp-chip">{signal.validationStatus.replaceAll("_", " ")}</span>
      {signal.strategyId ? <span className="tp-chip">{signal.strategyId}</span> : null}
      {signal.confidence ? <span className="tp-chip">{signal.confidence}</span> : null}
    </div>
    {signal.stance === "flat" ? (
      <p>{signal.thesisSummary || "No trade setup."}</p>
    ) : (
      <div className="tp-workbench-plan">
        <span>Entry: {signal.entryPrice ?? "—"}</span>
        <span>Stop: {signal.stopPrice ?? "—"}</span>
        <span>
          Targets:{" "}
          {signal.targetPrices.length > 0 ? signal.targetPrices.join(" / ") : "—"}
        </span>
        <span>R: {signal.riskRewardRatio ?? "—"}</span>
      </div>
    )}
    {signal.invalidationText ? (
      <p className="tp-muted">{signal.invalidationText}</p>
    ) : null}
    {signal.llmAdjustmentNote ? (
      <small className="tp-muted">{signal.llmAdjustmentNote}</small>
    ) : null}
  </div>
);
